import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '@v2/shared/lib/analytics';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { KustomerSearchHit, searchArticles } from './kustomer';
import { HelpKustomer } from './types';

/** The dropdown shows this many, per the brief. */
const SUGGESTIONS = 6;

/** Wait for a pause in typing before asking Kustomer. */
const DEBOUNCE_MS = 200;

export type SearchBoxProps = {
  action: string;
  /**
   * Both or neither: they are what the suggestions dropdown asks and labels with. /help
   * has them in its props. /contact passes neither and gets the same form without the
   * dropdown, because the titles come out of Kustomer's whole catalogue -- a call that
   * throws when Kustomer is down, which is not a thing to put in front of the contact page.
   */
  kustomer?: HelpKustomer;
  categoryTitles?: Record<string, string>;
  /** Prefilled on the results page, so the box still holds what was searched for. */
  query?: string;
};

/**
 * The search box in the band, with the suggestions dropdown behind it.
 *
 * A real form with a real action, first and always: it submits to /help/search and works
 * with no JS at all. The dropdown is an enhancement on top -- it asks Kustomer's search
 * directly as the reader types, and if that fails, pressing enter still works.
 *
 * Only hits in a category this help centre lists are shown, the same narrowing the
 * results page applies.
 */
export default function SearchBox({ action, kustomer, categoryTitles, query = '' }: SearchBoxProps) {
  const { buildUrl } = useUrlGenerator();
  const [term, setTerm] = useState(query);
  const [hits, setHits] = useState<KustomerSearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    // Close on a click anywhere else, which is the one behaviour a dropdown cannot do
    // from inside itself.
    const dismiss = (event: MouseEvent) => {
      if (container.current && !container.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', dismiss);

    return () => document.removeEventListener('mousedown', dismiss);
  }, [open]);

  useEffect(() => {
    const trimmed = term.trim();

    if (!kustomer || !categoryTitles || !open || trimmed === '') {
      setHits([]);

      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchArticles(kustomer, trimmed, controller.signal)
        .then((found) => setHits(found.filter((hit) => hit.slug !== '' && categoryTitles[hit.categoryId])))
        .catch(() => {
          // Aborted by the next keystroke, or Kustomer did not answer. Either way the
          // form still submits.
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, open, kustomer, categoryTitles]);

  const suggestions = hits.slice(0, SUGGESTIONS);

  return (
    <div ref={container} className="relative w-full max-w-2xl">
      <form
        action={buildUrl(action)}
        method="get"
        role="search"
        className="flex flex-col gap-2 md:flex-row"
        onSubmit={() => trackEvent('help_search', { query: term, results: suggestions.length })}
      >
        {/* No example query: a suggested search that finds nothing is worse than no suggestion. */}
        <input
          type="search"
          name="q"
          value={term}
          placeholder="Search the Help Center"
          aria-label="Search the Help Center"
          autoComplete="off"
          className="h-12 w-full min-w-0 rounded-lg border-0 bg-white px-4 text-base text-slate-900 md:flex-1"
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
        />
        <button
          type="submit"
          className="h-12 w-full shrink-0 cursor-pointer rounded-lg border-0 bg-slate-900 px-5 text-[15px] font-bold text-white md:w-auto"
        >
          Search
        </button>
      </form>

      {/*
       * `z-20`: the dropdown hangs out of the band and over the card below it. The card is
       * static today, but a positioned one would paint above an unindexed dropdown, and
       * nothing between here and the page root opens a stacking context, so the z-index
       * is what keeps the suggestions on top either way.
       *
       * `top-12` rather than `top-full`: under the input, not under the form. Below md the
       * button stacks beneath the input, and `top-full` would hang the suggestions under
       * the button instead. 3rem is the input's `h-12`, which from md up is also the
       * form's whole height, so there it is the same place `top-full` was.
       */}
      {open && suggestions.length > 0 ? (
        <ul className="absolute left-0 right-0 top-12 z-20 m-0 mt-1 list-none overflow-hidden rounded-lg border border-solid border-slate-200 bg-white p-0 text-left shadow-lg sm:right-auto sm:w-full">
          {suggestions.map((suggestion) => (
            <li key={suggestion.slug} className="m-0 border-0 border-b border-solid border-slate-100 last:border-b-0">
              <a
                href={buildUrl(`/help/${suggestion.slug}`)}
                className="block px-4 py-3 text-sm font-semibold text-slate-900 no-underline hover:bg-slate-50 hover:text-primary"
              >
                {suggestion.title}
                <span className="mt-0.5 block text-xs font-normal text-slate-500">
                  {categoryTitles[suggestion.categoryId]}
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
